<template>
  <div class="anchor-page">
    <!-- 头部标题栏 -->
    <div class="page-header">
      <h2>🎤 主播信息管理</h2>
    </div>

    <!-- 只保留新增按钮，删除搜索框 -->
    <div class="tool-bar">
      <div></div> <!-- 占位保持布局 -->
      <el-button type="primary" icon="plus" @click="openAddDialog">
        新增主播
      </el-button>
    </div>

    <!-- 表格 -->
    <div class="table-container">
      <el-table
        :data="anchorList"
        border
        stripe
        :loading="loading"
        style="width: 100%"
        row-class-name="table-row"
      >
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column prop="account" label="主播账号" min-width="150" />
        <el-table-column prop="password" label="密码" min-width="140" />
        <el-table-column prop="anchorCode" label="主播码" min-width="140" />

        <el-table-column label="操作" width="180" align="center">
          <template #default="scope">
            <el-button
              size="small"
              type="primary"
              icon="edit"
              @click="openEditDialog(scope.row)"
            >
              编辑
            </el-button>
            <el-button
              size="small"
              type="danger"
              icon="delete"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div class="pagination-box">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 30, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="getList"
        @current-change="getList"
      />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '✏️ 编辑主播' : '➕ 新增主播'"
      width="460px"
      @close="resetForm"
    >
      <el-form
        ref="formRef"
        :model="form"
        label-width="90px"
        :rules="rules"
      >
        <el-form-item label="主播名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入主播名称" />
        </el-form-item>

        <el-form-item label="主播账号" prop="key">
          <el-input v-model="form.key" placeholder="请输入主播账号" />
        </el-form-item>

        <el-form-item label="密码" prop="password">
          <el-input v-model="form.password" type="password" placeholder="请输入密码" show-password />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm">
            {{ isEdit ? '保存修改' : '确认新增' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

// ====================== 接口配置（你的真实接口）======================
const baseURL = 'https://api.wuxiahaojie.com/api'
const api = axios.create({
  baseURL: baseURL,
  timeout: 10000
})

// ====================== 数据 ======================
const loading = ref(false)
const anchorList = ref([])
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)

const dialogVisible = ref(false)
const isEdit = ref(false)
const formRef = ref(null)
const form = ref({
  id: '',
  name: '',
  key: '',
  password: ''
})

// 表单验证
const rules = {
  name: [{ required: true, message: '请输入主播名称', trigger: 'blur' }],
  key: [{ required: true, message: '请输入主播账号', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
}

// ====================== 获取列表 ======================
async function getList() {
  loading.value = true
  try {
    const res = await api.get('/Admin/GetAllList', {
      params: {
        page: currentPage.value,
        size: pageSize.value
      }
    })
    anchorList.value = res.data.list
    total.value = res.data.total
  } catch (err) {
    ElMessage.error('加载失败：' + err.message)
  } finally {
    loading.value = false
  }
}

// ====================== 新增 ======================
function openAddDialog() {
  isEdit.value = false
  form.value = { id: '', name: '', key: '', password: '' }
  dialogVisible.value = true
}

// ====================== 编辑 ======================
function openEditDialog(row) {
  isEdit.value = true
  form.value = {
    id: row.id,
    name: row.name || '',
    key: row.account || '',
    password: row.password || ''
  }
  dialogVisible.value = true
}

// ====================== 提交 ======================
async function submitForm() {
  if (!formRef.value) return
  await formRef.value.validate()
  try {
    const params = {
      name: form.value.name,
      key: form.value.key,
      password: form.value.password
    }
    
    if (isEdit.value) {
      // 编辑接口：/api/Admin/UpdateUser?id=1&name=1&key=1&password=1
      await api.get('/Admin/UpdateUser', {
        params: {
          id: form.value.id,
          ...params
        }
      })
      ElMessage.success('修改成功！')
    } else {
      // 新增接口：/api/Admin/AddUser?name=1&key=1&password=1
      await api.get('/Admin/AddUser', { params })
      ElMessage.success('新增成功！')
    }
    dialogVisible.value = false
    getList()
  } catch (e) {
    ElMessage.error('操作失败：' + (e.message || '请求异常'))
  }
}

// ====================== 删除 ======================
async function handleDelete(id) {
  try {
    await ElMessageBox.confirm('确定要删除该主播吗？', '提示', {
      type: 'warning'
    })
    // 删除接口：/api/Admin/DeleteUser?id=1
    await api.get('/Admin/DeleteUser', { params: { id } })
    ElMessage.success('删除成功')
    getList()
  } catch {
    ElMessage.info('已取消删除')
  }
}

// ====================== 重置 ======================
function resetForm() {
  formRef.value?.resetFields()
}

onMounted(() => getList())
</script>

<style scoped>
/* 整体页面 */
.anchor-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
}

/* 头部 */
.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1f2329;
}

/* 工具栏 */
.tool-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

/* 表格 */
.table-container {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}

:deep(.el-table) {
  --el-table-header-text-color: #4e5969;
  --el-table-header-bg-color: #fafbfc;
}

.table-row {
  cursor: pointer;
}

/* 分页 */
.pagination-box {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 弹窗底部 */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>